import type { Config, Dimension } from '@dashboard/types/config'
import type { FunnelDefinitionNode, FunnelDefinitionRootNode } from '@dashboard/modules/flow/builder/types/nodes'
import { stripHtml } from './services/text'

type RepairConfig = Config | null | undefined
type TraversableNode = FunnelDefinitionNode | FunnelDefinitionRootNode

// Match dashboard traversal: visit parents first, runs in reverse order, and
// branches in stored order. Repairs can remove a branch before it is visited.
const forEachFlowNode = (config: RepairConfig, visit: (node: TraversableNode) => void) => {
  const visitRun = (run: FunnelDefinitionNode[]) => {
    for (let index = run.length - 1; index >= 0; index--) {
      const node = run[index]
      if (node === undefined) {
        throw new Error('Missing node in funnel definition run')
      }
      visitNode(node)
    }
  }

  const visitNode = (node: TraversableNode) => {
    visit(node)

    if (node.type === 'root') {
      visitRun(node.child || [])
      return
    }

    if (node.type === 'sg' || node.type === 'random' || node.type === 'highscore') {
      if (node.split) {
        node.split.forEach((split) => visitRun(split.child))
      }
      return
    }

    if (node.type === 'yesno') {
      visitRun(node.yes)
      visitRun(node.no)
    }
  }

  config?.rightCta?.offerFunnels?.forEach((flow) => {
    visitNode(flow.funnelDefinition as FunnelDefinitionRootNode)
  })
}

/** Reorder router branches in place, retaining unknown branches and default paths. */
export const syncRouterSplitOrder = (config: RepairConfig, segmentGroup: Dimension): void => {
  const segmentIds = segmentGroup.segments.map((segment) => segment.id)

  forEachFlowNode(config, (node) => {
    if (node.type === 'sg' && node.split && Array.isArray(node.split) && node.segmentGroupId === segmentGroup.id) {
      const defaultPaths = node.split.filter((split) => split.segmentId === null)
      const segmentPaths = node.split.filter((split) => split.segmentId !== null)
      const reordered = segmentIds
        .map((id) => segmentPaths.find((path) => path.segmentId === id))
        .filter((path) => path !== undefined)
      const orphaned = segmentPaths.filter(
        (path) => path.segmentId === null || path.segmentId === undefined || !segmentIds.includes(path.segmentId)
      )

      node.split = [...reordered, ...orphaned, ...defaultPaths]
    }
  })
}

/** Backfill question flags in place using the dashboard's DOM text-content semantics. */
export const repairQuestions = (config: RepairConfig): void => {
  config?.rightAsk?.questions?.forEach((question) => {
    if (question.isQuestion !== true && question.text && stripHtml(question.text).trim() !== '') {
      question.isQuestion = true
    }
  })
}

/** Reconcile flow router branches with dimension segments without stores or autosaving. */
export const repairFlowSplits = (config: RepairConfig): void => {
  const repairedSegmentGroupIds = new Set<string>()
  let segmentGroups: Dimension[] | undefined
  const getSegmentGroup = (id: string) => {
    // The dashboard's segmentGroupStore lookup sorts the live dimension array.
    // Preserve that normalization, but only when a router actually needs a lookup.
    segmentGroups ??= (config?.dimensions || []).sort((a, b) => a.name.localeCompare(b.name))
    return segmentGroups.find((segmentGroup) => segmentGroup.id === id)
  }

  forEachFlowNode(config, (node) => {
    if (node.type !== 'sg' || !node.split || !Array.isArray(node.split) || !node.segmentGroupId) {
      return
    }

    const segmentGroup = getSegmentGroup(node.segmentGroupId)
    if (!segmentGroup) {
      return
    }

    const actualSegmentIds = segmentGroup.segments.map((segment) => segment.id)
    const existingSplitSegmentIds = node.split.map((split) => split.segmentId).filter(Boolean)

    // The tag prefers any matching answer branch over the fallback, so an empty
    // branch would end the flow for answers the fallback already routes (ENG-1596).
    // Without a fallback, keep the legacy insertion position before the last branch.
    const hasFallback = node.split.some((split) => split.segmentId === null)
    const missingIds = hasFallback ? [] : actualSegmentIds.filter((id) => !existingSplitSegmentIds.includes(id))
    for (const segmentId of missingIds) {
      const insertPos = Math.max(0, node.split.length - 1)
      node.split.splice(insertPos, 0, { segmentId, child: [] })
    }

    const orphanedCount = node.split.filter(
      (split) => split.segmentId && !actualSegmentIds.includes(split.segmentId)
    ).length
    if (orphanedCount > 0) {
      node.split = node.split.filter((split) => !split.segmentId || actualSegmentIds.includes(split.segmentId))
    }

    if (missingIds.length > 0 || orphanedCount > 0) {
      repairedSegmentGroupIds.add(node.segmentGroupId)
    }
  })

  // A membership repair normalizes every router for that dimension. Mere order
  // differences are deliberately left untouched when no membership was repaired.
  for (const id of repairedSegmentGroupIds) {
    const segmentGroup = getSegmentGroup(id)
    if (segmentGroup) {
      syncRouterSplitOrder(config, segmentGroup)
    }
  }
}
